import type { TOC } from '@ember/component/template-only';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { isNone } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import svgJar from 'ember-svg-jar/helpers/svg-jar';

import FileUploader from 'umg-ember-backend/components/ui/file-uploader';
import type AssetUploaderService from 'umg-ember-backend/services/asset-uploader';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import type {
  AssetFolder,
  SetAssetFolder,
  ValidateAssetUpload,
} from '../../types';
import styles from './styles.module.css';

const bem = getBem(styles);

interface FolderLabelSignature {
  Args: {
    folder: AssetFolder;
    hasChildren: boolean;
    isDisabled: boolean;
    isOpen: boolean;
    openFolder: () => boolean | void;
    openOrSelect: SetAssetFolder;
  };
  Element: HTMLButtonElement;
}

const FolderLabel = <template>
  <button
    type='button'
    class={{bem 'label'}}
    disabled={{@isDisabled}}
    aria-expanded={{if @hasChildren (if @isOpen 'true' 'false')}}
    data-test-folder-label
    {{on 'click' (fn @openOrSelect @folder.value)}}
    {{on 'dragenter' @openFolder}}
  >
    <span class={{bem 'icon'}}>
      {{svgJar (if @isOpen 'folder-open' 'folder') class='u-icon'}}
    </span>
    <span class={{bem 'name'}}>
      {{@folder.label}}
    </span>
    {{#if @hasChildren}}
      {{svgJar 'chevron-right' class=(bem 'chevron')}}
    {{/if}}
  </button>
</template> satisfies TOC<FolderLabelSignature>;

interface FolderSignature {
  Args: {
    allowedFolder?: string;
    folder: AssetFolder;
    onValidateUpload?: ValidateAssetUpload;
    selectedFolderId?: string;
    setFolder: SetAssetFolder;
  };
  Element: HTMLLIElement;
}

export default class RecordExplorerForAssetFoldersFolderComponent extends Component<FolderSignature> {
  // Services
  @service declare assetUploader: AssetUploaderService;

  // Folder expansion
  @tracked _isOpen: boolean | null = null;

  get hasChildren() {
    return !!this.args.folder.children;
  }

  get isOpen() {
    const selectedFolderId = this.args.selectedFolderId;
    const children = this.args.folder.children;
    const isOpen = this._isOpen;

    if (!isNone(isOpen)) {
      return isOpen;
    }

    const isChildSelected = children?.find((child) => {
      return child.value === selectedFolderId;
    });

    return Boolean(children && isChildSelected);
  }

  openFolder = () => {
    if (this.isDisabled) {
      return false;
    }

    if (this.hasChildren) {
      this._isOpen = true;
    }
  };

  // Folder selection
  get isSelected() {
    const folderId = this.args.folder.value;
    const selectedFolderId = this.args.selectedFolderId;

    return folderId === selectedFolderId;
  }

  get isDisabled() {
    return this.isFolderDisabled(this.args.folder);
  }

  // Functions
  isFolderDisabled = (folder: AssetFolder) => {
    const { allowedFolder } = this.args;

    if (!allowedFolder) {
      return false;
    }

    if (folder.children) {
      return allowedFolder !== folder.value;
    }

    return allowedFolder !== folder.parent && allowedFolder !== folder.value;
  };

  openOrSelect: SetAssetFolder = (folderId) => {
    if (this.isDisabled) {
      return false;
    }

    if (this.hasChildren) {
      if (isNone(this._isOpen)) {
        this._isOpen = this.isOpen;
      }

      this._isOpen = !this._isOpen;
    } else {
      this.args.setFolder(folderId);
    }
  };

  // Folder uploads
  get canUpload() {
    return !this.hasChildren && !isNone(this.args.folder.value);
  }

  // Template
  <template>
    <li
      class={{bem
        (hash
          is-disabled=this.isDisabled
          is-open=this.isOpen
          is-selected=this.isSelected
          has-children=this.hasChildren
        )
      }}
      data-test-folder-id={{@folder.value}}
      ...attributes
    >
      {{#if this.canUpload}}
        <FileUploader
          @isDisabled={{this.isDisabled}}
          @isMultiple={{true}}
          @onSuccess={{this.assetUploader.saveFile}}
          @onUploadStart={{fn this.assetUploader.addFile @folder}}
          @onDragEnter={{fn this.assetUploader.showHint @folder}}
          @onDragLeave={{this.assetUploader.hideHint}}
          @onValidateUpload={{@onValidateUpload}}
          data-folder-uploader
          data-test-folder-uploader
        >
          <FolderLabel
            @folder={{@folder}}
            @hasChildren={{this.hasChildren}}
            @isDisabled={{this.isDisabled}}
            @isOpen={{this.isOpen}}
            @openFolder={{this.openFolder}}
            @openOrSelect={{this.openOrSelect}}
          />
        </FileUploader>
      {{else}}
        <FolderLabel
          @folder={{@folder}}
          @hasChildren={{this.hasChildren}}
          @isDisabled={{this.isDisabled}}
          @isOpen={{this.isOpen}}
          @openFolder={{this.openFolder}}
          @openOrSelect={{this.openOrSelect}}
        />
      {{/if}}
      {{#if @folder.children}}
        <ul class={{bem 'children'}} data-test-folder-children>
          {{#each @folder.children as |childFolder|}}
            <RecordExplorerForAssetFoldersFolderComponent
              @allowedFolder={{@allowedFolder}}
              @folder={{childFolder}}
              @onValidateUpload={{@onValidateUpload}}
              @selectedFolderId={{@selectedFolderId}}
              @setFolder={{@setFolder}}
            />
          {{/each}}
        </ul>
      {{/if}}
    </li>
  </template>
}
